import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FiChevronLeft,
  FiChevronRight,
  FiCalendar,
  FiList,
  FiCheck,
  FiPlay,
} from "react-icons/fi";
import ReadingPlanUnavailable from "./ReadingPlanUnavailable";

const NEU_RAISED_LG =
  "shadow-[-10px_-10px_22px_var(--outer-shadow-light),10px_10px_22px_var(--outer-shadow-dark)]";
const NEU_RAISED_MD =
  "shadow-[-8px_-8px_18px_var(--outer-shadow-light),8px_8px_18px_var(--outer-shadow-dark)]";
const NEU_INSET_SM =
  "shadow-[inset_2px_2px_4px_var(--shadow-dark),inset_-2px_-2px_4px_var(--shadow-light)]";
const PAGE_RAISED_SM =
  "shadow-[-4px_-4px_9px_var(--outer-shadow-light),4px_4px_9px_var(--outer-shadow-dark)]";

const clientTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (_e) {
    return null;
  }
};

const isoOf = (y, m, d) =>
  `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const firstOfMonth = (y, m) => isoOf(y, m, 1);
const lastOfMonth = (y, m) => {
  const d = new Date(y, m, 0).getDate(); // 0 of next month = last of current
  return isoOf(y, m, d);
};
const shiftMonth = (year, month, delta) => {
  const total = year * 12 + (month - 1) + delta;
  return { year: Math.floor(total / 12), month: (total % 12) + 1 };
};

const monthLabel = (year, month) =>
  new Date(year, month - 1, 1).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
  });

const ReadingPlanOverview = () => {
  const { planSlug } = useParams();
  const navigate = useNavigate();

  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem("bibleMemosTheme") || "powder";
    } catch (_e) {
      return "powder";
    }
  });
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "bibleMemosTheme" && e.newValue) setTheme(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const tz = useMemo(clientTimezone, []);
  const commonHeaders = useMemo(
    () => (tz ? { "X-Client-Timezone": tz } : {}),
    [tz]
  );

  const [planMeta, setPlanMeta] = useState(null);
  const [progress, setProgress] = useState(null);
  const [today, setToday] = useState(null);
  const [view, setView] = useState("list");
  const [planLoaded, setPlanLoaded] = useState(false);

  // Month currently being browsed. Defaults to today (from the server).
  const [month, setMonth] = useState(null);
  const [days, setDays] = useState([]);
  const [monthLoading, setMonthLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load plan meta, progress, today once.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [planRes, progRes, todayRes] = await Promise.all([
          fetch(`/api/reading-plans/${planSlug}`, { headers: commonHeaders, credentials: "include" }),
          fetch(`/api/reading-plans/${planSlug}/progress`, { headers: commonHeaders, credentials: "include" }),
          fetch(`/api/reading-plans/${planSlug}/today`, { headers: commonHeaders, credentials: "include" }),
        ]);
        const [plan, prog, td] = await Promise.all([
          planRes.ok ? planRes.json() : null,
          progRes.ok ? progRes.json() : null,
          todayRes.ok ? todayRes.json() : null,
        ]);
        if (cancelled) return;
        setPlanMeta(plan);
        setProgress(prog);
        setToday(td);
        // Default month to today's month (if plan spans it) or the plan year.
        if (td?.date) {
          const [y, m] = td.date.split("-").map(Number);
          setMonth({ year: y, month: m });
        } else if (plan?.year) {
          setMonth({ year: plan.year, month: 1 });
        } else {
          const now = new Date();
          setMonth({ year: now.getFullYear(), month: now.getMonth() + 1 });
        }
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load plan");
      } finally {
        if (!cancelled) setPlanLoaded(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [planSlug, commonHeaders]);

  // Load the current month's days on month change.
  const loadMonth = useCallback(async () => {
    if (!month) return;
    setMonthLoading(true);
    setError(null);
    try {
      const start = firstOfMonth(month.year, month.month);
      const end = lastOfMonth(month.year, month.month);
      const res = await fetch(
        `/api/reading-plans/${planSlug}/days?start=${start}&end=${end}`,
        { headers: commonHeaders, credentials: "include" }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to load month");
      }
      const rows = await res.json();
      setDays(rows);
    } catch (err) {
      setError(err.message);
    } finally {
      setMonthLoading(false);
    }
  }, [month, planSlug, commonHeaders]);
  useEffect(() => { loadMonth(); }, [loadMonth]);

  // Group segments by date so multi-passage days render as one row/cell.
  const daysByDate = useMemo(() => {
    const map = new Map();
    for (const day of days) {
      if (!map.has(day.date)) map.set(day.date, []);
      map.get(day.date).push(day);
    }
    return map;
  }, [days]);

  const completedDayIds = useMemo(
    () => new Set(progress?.completed_day_ids || []),
    [progress]
  );
  const isDateComplete = useCallback(
    (dateISO) => {
      const segs = daysByDate.get(dateISO);
      if (!segs || segs.length === 0) return false;
      return segs.every((s) => completedDayIds.has(s.id));
    },
    [daysByDate, completedDayIds]
  );

  const todayISO = today?.date || null;

  const gotoDate = (dateISO) => {
    if (!dateISO) return;
    navigate(`/bible-reading/plan/${planSlug}/${dateISO}`);
  };

  const goPrevMonth = () => setMonth((m) => (m ? shiftMonth(m.year, m.month, -1) : m));
  const goNextMonth = () => setMonth((m) => (m ? shiftMonth(m.year, m.month, 1) : m));
  const jumpToday = () => {
    if (!todayISO) return;
    const [y, m] = todayISO.split("-").map(Number);
    setMonth({ year: y, month: m });
    // Scroll into view is best-effort; the list highlights today's row.
  };

  const percentText = progress
    ? `${progress.completed_count} / ${progress.total_count} · ${progress.percent}%`
    : "—";

  // If the plan endpoint returned no data (missing, unpublished, wrong slug),
  // show a friendly full-page card instead of an "empty" header + broken list.
  if (planLoaded && !planMeta) {
    return (
      <ReadingPlanUnavailable
        theme={theme}
        message="This plan isn't published yet or the link is off. Browse other plans, or head to today's devotional."
      />
    );
  }

  return (
    <div
      data-theme={theme}
      className="min-h-screen bg-page-bg text-on-surface font-inter transition-colors"
    >
      <main className="w-full">
        <div className="w-full max-w-[1440px] mx-auto px-margin-mobile md:px-margin-neu py-space-xl flex flex-col gap-space-xl">
          {/* Header card */}
          <section
            className={`rounded-[2rem] bg-canvas-base p-space-lg md:p-space-xl ${NEU_RAISED_LG} flex flex-col gap-space-md`}
          >
            <div className="flex flex-col gap-space-xs">
              <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
                Reading Plan
              </span>
              <h1 className="font-noto-serif text-headline-lg text-neu-primary">
                {planMeta?.name || "Reading Plan"}
              </h1>
              <p className="text-body-lg text-slate-muted">
                Year {planMeta?.year || "—"} · {percentText}
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <div className={`w-full h-3 rounded-full bg-canvas-base p-0.5 ${NEU_INSET_SM}`}>
                <div
                  className="h-full rounded-full bg-neu-secondary transition-all"
                  style={{ width: `${progress?.percent || 0}%` }}
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-space-sm">
              {todayISO && (
                <Link
                  to={`/bible-reading/plan/${planSlug}/${todayISO}`}
                  className="inline-flex items-center gap-2 h-11 px-space-lg rounded-full bg-accent-cta text-white text-label-md font-semibold shadow-[-3px_-3px_7px_var(--outer-shadow-light),4px_5px_10px_rgba(0,0,0,0.22)] hover:bg-accent-cta-hover active:shadow-[inset_2px_2px_5px_rgba(0,0,0,0.3),inset_-2px_-2px_5px_rgba(255,255,255,0.2)] transition-all"
                >
                  <FiPlay />
                  <span>Continue Reading</span>
                </Link>
              )}
              <button
                type="button"
                onClick={jumpToday}
                disabled={!todayISO}
                className={`inline-flex items-center gap-1 h-11 px-space-md rounded-full bg-canvas-base text-neu-primary text-label-md font-semibold ${PAGE_RAISED_SM} hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_5px_var(--outer-shadow-dark),inset_-2px_-2px_5px_var(--outer-shadow-light)] transition-all disabled:opacity-50`}
              >
                Today
              </button>

              <div className={`ml-auto inline-flex items-center gap-1 p-1 rounded-full bg-canvas-base ${NEU_INSET_SM}`}>
                {["list", "calendar"].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setView(v)}
                    aria-pressed={view === v}
                    className={
                      view === v
                        ? `flex items-center gap-1 px-space-sm py-1.5 rounded-full bg-canvas-base ${PAGE_RAISED_SM} text-label-sm text-neu-primary font-bold`
                        : "flex items-center gap-1 px-space-sm py-1.5 rounded-full text-label-sm text-slate-muted hover:text-neu-primary transition-colors"
                    }
                  >
                    {v === "list" ? <FiList /> : <FiCalendar />}
                    <span className="capitalize">{v}</span>
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Month navigator */}
          <section className={`rounded-[2rem] bg-canvas-base p-space-lg ${NEU_RAISED_MD} flex flex-col gap-space-md`}>
            <div className="flex items-center justify-between gap-space-sm">
              <button
                type="button"
                onClick={goPrevMonth}
                aria-label="Previous month"
                className={`w-11 h-11 rounded-full flex items-center justify-center bg-canvas-base text-neu-primary ${PAGE_RAISED_SM} hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_5px_var(--outer-shadow-dark),inset_-2px_-2px_5px_var(--outer-shadow-light)] transition-all`}
              >
                <FiChevronLeft />
              </button>
              <h2 className="font-noto-serif text-headline-md text-neu-primary text-center flex-1">
                {month ? monthLabel(month.year, month.month) : "…"}
              </h2>
              <button
                type="button"
                onClick={goNextMonth}
                aria-label="Next month"
                className={`w-11 h-11 rounded-full flex items-center justify-center bg-canvas-base text-neu-primary ${PAGE_RAISED_SM} hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_5px_var(--outer-shadow-dark),inset_-2px_-2px_5px_var(--outer-shadow-light)] transition-all`}
              >
                <FiChevronRight />
              </button>
            </div>

            {error && (
              <p className="text-body-md text-[#b02a37]">{error}</p>
            )}
            {monthLoading && (
              <p className="text-body-md text-slate-muted">Loading…</p>
            )}

            {!monthLoading && !error && view === "list" && (
              <ListView
                daysByDate={daysByDate}
                todayISO={todayISO}
                isDateComplete={isDateComplete}
                onOpen={gotoDate}
                planSlug={planSlug}
              />
            )}

            {!monthLoading && !error && view === "calendar" && month && (
              <CalendarView
                year={month.year}
                month={month.month}
                daysByDate={daysByDate}
                todayISO={todayISO}
                isDateComplete={isDateComplete}
                onOpen={gotoDate}
              />
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

const ListView = ({ daysByDate, todayISO, isDateComplete, onOpen }) => {
  const dates = Array.from(daysByDate.keys()).sort();
  if (!dates.length) {
    return <p className="text-body-md text-slate-muted">No readings this month.</p>;
  }
  return (
    <ul className="flex flex-col divide-y divide-[color:var(--outer-shadow-dark)]/20">
      {dates.map((dateISO) => {
        const segs = daysByDate.get(dateISO) || [];
        const complete = isDateComplete(dateISO);
        const isToday = dateISO === todayISO;
        const references = segs
          .map((s) => s.reference_en || s.reference_ko)
          .join(" · ");
        const weekday = new Date(`${dateISO}T00:00:00`).toLocaleDateString(undefined, {
          weekday: "short",
        });
        return (
          <li
            key={dateISO}
            className={`flex items-center justify-between gap-space-sm py-space-sm ${
              isToday ? "bg-canvas-base/60 -mx-2 px-2 rounded-lg" : ""
            }`}
          >
            <div className="flex items-center gap-space-sm min-w-0">
              <div
                className={`w-11 h-11 flex-shrink-0 rounded-full flex items-center justify-center text-label-md font-semibold ${
                  complete
                    ? "bg-accent-cta text-white"
                    : isToday
                      ? "bg-canvas-base text-accent-cta border-2 border-accent-cta"
                      : "bg-canvas-base text-slate-muted shadow-[inset_2px_2px_4px_var(--shadow-dark),inset_-2px_-2px_4px_var(--shadow-light)]"
                }`}
                aria-label={
                  complete ? "Completed" : isToday ? "Today, incomplete" : "Incomplete"
                }
              >
                {complete ? <FiCheck /> : dateISO.slice(-2)}
              </div>
              <div className="flex flex-col min-w-0">
                <p className="text-label-md text-neu-primary font-semibold truncate">
                  {references}
                </p>
                <p className="text-label-sm text-slate-muted">
                  {dateISO} · {weekday}
                  {segs.length > 1 ? ` · ${segs.length} passages` : ""}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onOpen(dateISO)}
              className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-canvas-base text-neu-primary text-label-sm font-semibold shadow-[-2px_-2px_4px_var(--outer-shadow-light),2px_2px_4px_var(--outer-shadow-dark)] hover:shadow-[-1px_-1px_2px_var(--outer-shadow-light),1px_1px_2px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_4px_var(--outer-shadow-dark),inset_-2px_-2px_4px_var(--outer-shadow-light)] transition-all"
            >
              {complete ? "Review" : "Read"}
            </button>
          </li>
        );
      })}
    </ul>
  );
};

const CalendarView = ({ year, month, daysByDate, todayISO, isDateComplete, onOpen }) => {
  const firstWeekday = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7) cells.push(null);
  return (
    <div>
      <div className="grid grid-cols-7 gap-1 text-center text-label-sm font-semibold text-slate-muted mb-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((w) => (
          <span key={w}>{w}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} className="min-h-[80px]" aria-hidden="true" />;
          const dateISO = isoOf(year, month, d);
          const segs = daysByDate.get(dateISO) || [];
          const complete = isDateComplete(dateISO);
          const isToday = dateISO === todayISO;
          const disabled = segs.length === 0;
          const refList = segs.map((s) => s.reference_en || s.reference_ko);
          const ref = refList.join(" · ");
          // Spelled-out accessible label — screen-reader users get the same
          // information sighted users see in the tooltip, plus completion +
          // today state.
          const spokenDate = new Date(`${dateISO}T00:00:00`).toLocaleDateString(undefined, {
            month: "long",
            day: "numeric",
          });
          const spokenRefs =
            refList.length === 0
              ? "no scheduled reading"
              : refList.length === 1
                ? refList[0]
                : `${refList.slice(0, -1).join(", ")}, and ${refList[refList.length - 1]}`;
          const spokenStatus = disabled
            ? ""
            : complete
              ? ". Completed"
              : isToday
                ? ". Today, not completed"
                : ". Not completed";
          const ariaLabel = `${spokenDate}. ${spokenRefs}${spokenStatus}.`;
          return (
            <button
              key={dateISO}
              type="button"
              disabled={disabled}
              onClick={() => onOpen(dateISO)}
              className={`min-h-[80px] flex flex-col items-start justify-between rounded-xl p-2 text-left transition-all ${
                disabled
                  ? "bg-canvas-base opacity-40"
                  : complete
                    ? "bg-accent-cta text-white shadow-[-2px_-2px_5px_var(--outer-shadow-light),3px_3px_7px_rgba(0,0,0,0.22)] hover:bg-accent-cta-hover"
                    : isToday
                      ? "bg-canvas-base border-2 border-accent-cta text-neu-primary"
                      : "bg-canvas-base text-neu-primary shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] hover:shadow-[-1px_-1px_3px_var(--outer-shadow-light),1px_1px_3px_var(--outer-shadow-dark)]"
              }`}
              aria-label={ariaLabel}
              aria-current={isToday ? "date" : undefined}
            >
              <div className="flex items-center justify-between w-full">
                <span className={`text-label-md font-bold ${complete ? "text-white" : "text-neu-primary"}`} aria-hidden="true">
                  {d}
                </span>
                {complete && <FiCheck className="text-white" aria-hidden="true" />}
              </div>
              <span
                className={`text-label-sm truncate w-full ${complete ? "text-white/90" : "text-slate-muted"}`}
                title={ref}
                aria-hidden="true"
              >
                {segs.length > 1 ? `${segs[0].reference_en || segs[0].reference_ko} +${segs.length - 1} more` : ref}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ReadingPlanOverview;
